import { fabric } from 'fabric';
import {
  hasCommentableBehaviors,
  hasFrameBehaviors,
  hasFrameMemberBehaviors,
  isFrameLike,
  isSimpleGroup,
  hasLayout,
  isFrame,
} from './guards';
import { type MountedObjects } from '@lib/canvasx/canvasx';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type Constructor } from '../shared-types';
import { type MoveEvent } from '../external-types';
import type FrameMemberBehaviors from './FrameMemberBehaviors';
import * as Sentry from '@sentry/browser';
import type Frame from './Frame';
import { type IPoint, type Point } from 'fabric/fabric-impl';

export const HOVER_SHADOW_COLOR = '#4273FB';

export const isEnabledFrame = (
  obj: fabric.Object
): obj is fabric.Object & FrameBehaviors =>
  hasFrameBehaviors(obj) && obj.frameBehaviorsEnabled;

interface FrameBehaviors {
  enableFrameBehaviors(): void;
  disableFrameBehaviors(): void;
  startMoveWithContents(eventData?: fabric.IEvent<MouseEvent>): void;
  moveWithContents(): void;
  movedWithContents(eventData?: fabric.IEvent<MouseEvent>): void;
  parentFrame: (FrameBehaviors & fabric.Object) | undefined;
  childFrames: Frame[];
  readonly children: fabric.Object[];
  setHovered(e: MoveEvent): boolean;
  clearHovered(): void;
  hovered: boolean;
  edgesVisible: boolean;
  addMember(object: fabric.Object): void;
  removeMember(object: fabric.Object): void;
  readonly frameBehaviorsEnabled: boolean;
  layoutPaddingSides(): SpacingSidesStruct;
  frameContainsPoint(
    pointer: IPoint,
    lines?: any,
    absolute?: boolean,
    calculate?: boolean
  ): boolean;
  captureContents(): void;

  isInFrameHierarchy(
    targetFrame: FrameBehaviors & fabric.Object,
    layoutFrame: FrameBehaviors & fabric.Object,
    recursionDepth?: number
  ): boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function FrameBehaviors(
  Base: Constructor<fabric.Object & { serializedKeys: Array<string | RegExp> }>
): any {
  return class extends Base implements FrameBehaviors {
    __hasFrameBehaviors = true;
    frameBehaviorsEnabled: boolean;

    mountedObjects: MountedObjects = {};

    lastLeft: number | undefined;
    lastTop: number | undefined;

    // Whether another object is currently being dragged over this frame.
    hovered = false;

    // For Tables and Frames, we track whether the edges of the object are in the
    // current viewport. These two object types tend to get quite large, so we
    // change their selection behavior when they are zoomed in
    edgesVisible = true;

    // Keys that will be serialized for saving and sending to remote systems.
    serializedKeys = [...this.serializedKeys, 'layoutPosition', 'layoutManual'];

    // Array of objects in frame
    private _containedObjects: fabric.Object[];

    constructor(options) {
      super(options);

      // List of objects contained in this frame. Only valid during a move.
      this._containedObjects = [];
    }

    // Some objects will optionally enable this behavior for itself or only
    // certain subclasses.
    enableFrameBehaviors() {
      if (!this.frameBehaviorsEnabled) {
        this.frameBehaviorsEnabled = true;
        this.on('mousedown', this.onFrameMouseDown);
        this.on('moving', this.onFrameMoving);
        this.on('moved', this.onFrameMoved);
        this.on('scaled', this.onFrameScaled);
      }
    }

    disableFrameBehaviors() {
      this.frameBehaviorsEnabled = false;
      this.off('mousedown', this.onFrameMouseDown);
      this.off('moving', this.onFrameMoving);
      this.off('moved', this.onFrameMoved);
      this.off('scaled', this.onFrameScaled);
    }

    deleted(controller: WhiteboardController) {
      if (this.canvas) {
        this.removeChildrenFromFrame();
      }
      super.deleted(controller);
    }

    // Collect the objects that are in this frame in anticipation of moving them.
    startMoveWithContents(eventData?: fabric.IEvent<MouseEvent>) {
      const absPosition = this.absolutePosition();
      this.lastLeft = absPosition.left;
      this.lastTop = absPosition.top;

      // If the user holds down Ctrl or Option allow moving the frame without any
      // objects. The Option case means that when duplicating a frame we do not
      // drag any non-selected objects.
      this._containedObjects = this.containedChildren
        ? this.containedChildren()
        : this.children;

      // Make sure groups are ready to be moved.
      this._containedObjects.forEach(obj => {
        if (hasFrameBehaviors(obj)) {
          obj.startMoveWithContents(eventData);
        }
        if (hasCommentableBehaviors(obj)) {
          obj.prepareContainedComments(false);
          obj.startMoveWithComments();
        }

        if (isSimpleGroup(obj)) {
          obj.prepareForTransform(eventData);
        } else {
          obj._currentTransform = {
            original: fabric.util.saveObjectTransform(obj),
            last: fabric.util.saveObjectTransform(obj),
          };
        }
      });
    }

    // Find any objects overlapping the frame and move them too.
    moveWithContents() {
      const absPosition = this.absolutePosition();
      const deltaX = absPosition.left - (this.lastLeft ?? 0);
      const deltaY = absPosition.top - (this.lastTop ?? 0);

      // moveWithContents is triggered many more times than needed, so bail out if there's no delta
      if (deltaX || deltaY) {
        this.lastLeft = absPosition.left;
        this.lastTop = absPosition.top;

        this._containedObjects.forEach(obj => {
          obj.set({ left: obj.left + deltaX, top: obj.top + deltaY });
          obj.setCoords();
          obj.fire('moving', {
            transform: obj._currentTransform,
            frameMoving: this.id,
          });
          if (obj.type !== 'group') {
            if (!obj._currentTransform) {
              Sentry.captureException(
                new Error('Missing transform for object in frame on moved')
              );
              return;
            }
            obj._currentTransform.last = fabric.util.saveObjectTransform(obj);
          }
        });
      }
    }

    movedWithContents(eventData?: fabric.IEvent<MouseEvent>) {
      this._containedObjects.forEach(obj => {
        obj.fire('moved', {
          ...eventData,
          frameMoving: this.id,
          transform: obj._currentTransform,
        });
      });
    }

    setHovered(e: MoveEvent): boolean {
      if (super.setHovered(e)) {
        this.hovered = true;
      }

      return this.hovered;
    }

    clearHovered() {
      super.clearHovered();
      this.hovered = false;
    }

    addMember(object: fabric.Object & FrameMemberBehaviors) {
      if (this.frameBehaviorsEnabled && object.container !== this.id) {
        if (object.container) {
          const container = object.inFrame();
          container?.fire('member:removed', {
            target: object,
            currentTarget: container,
          });
        }
        object.set({ container: this.id, layoutPosition: null });
        this.canvas.collectionNeedsSort = true;
        this.fire('member:added', { target: object, currentTarget: this });
      }
    }

    removeMember(object: fabric.Object & FrameMemberBehaviors) {
      object.set({ container: null, layoutPosition: null });
      this.canvas.collectionNeedsSort = true;
      this.fire('member:removed', { target: object, currentTarget: this });
    }

    // Check if the point is inside the frame at all.
    frameContainsPoint(
      pointer: Point,
      lines?: any,
      absolute?: boolean,
      calculate?: boolean
    ) {
      if (isFrame(this)) {
        return this.everContainsPoint(pointer, lines, absolute, calculate);
      }
      return super.containsPoint(pointer, lines, absolute, calculate);
    }

    // Finds objects with this object as its container
    get children(): (FrameMemberBehaviors & fabric.Object)[] {
      if (!this.canvas) return [];

      return this.canvas
        .getObjects()
        .filter(
          (obj): obj is FrameMemberBehaviors & fabric.Object =>
            hasFrameMemberBehaviors(obj) &&
            obj.container === this.id &&
            obj.id !== this.id
        );
    }

    get parentFrame(): (FrameBehaviors & fabric.Object) | undefined {
      if (hasFrameMemberBehaviors(this) && this.container) {
        const parent = this.controller.dataStoreObjects.findRecordById(
          this.container
        );
        if (hasFrameBehaviors(parent)) {
          return parent;
        }
      }
    }

    // TODO This is slightly out of place here. It is only used in LayoutBehaviors
    // and acutally needs to get the list of Frames on the page, or really, the
    // layouts.
    get childFrames(): Frame[] {
      return this.children.filter(isFrameLike);
    }

    layoutPaddingSides(): SpacingSidesStruct {
      return { left: 0, top: 0, right: 0, bottom: 0 };
    }

    // After scaling this frame, this frame may no longer be in its previous
    // parent frame. We also do this at the start of a move for any existing stale
    // container values
    private removeFromParentFrame() {
      if (hasFrameMemberBehaviors(this)) {
        if (this.container) {
          const parentFrame = this.canvas._objects.find(
            obj => obj.id === this.container
          );

          if (hasFrameBehaviors(parentFrame)) {
            if (
              !parentFrame.containsObject(this) &&
              this.isFrameAllowed(undefined)
            ) {
              parentFrame.removeMember(this);
            }
          }
        }
      }
    }

    // When removed, free contained objects
    private removeChildrenFromFrame() {
      this.children.forEach(child => {
        if (!child.isFrameAllowed(undefined)) {
          return;
        }
        this.removeMember(child);
      });
    }

    // When frame resizes (rescales), remove any objects that might now be outside
    // the frame's boundaries
    captureContents() {
      // Don't add objects to the frame if it is auto-layout.
      if (hasLayout(this)) return;

      // Remove old objects.
      this.children.forEach(obj => {
        if (!this.containsObject(obj) && obj.isFrameAllowed(undefined)) {
          this.removeMember(obj);
        }
      });

      const container = hasFrameMemberBehaviors(this) ? this.container : null;

      // Find new objects that overlap with this frame and are not in another frame.
      this.canvas
        .getObjects()
        .filter(
          (obj): obj is FrameMemberBehaviors & fabric.Object =>
            obj.id !== this.id &&
            hasFrameMemberBehaviors(obj) &&
            !obj.container &&
            container !== obj.id &&
            obj.isFrameAllowed(this) &&
            this.containsObject(obj)
        )
        .forEach(obj => this.addMember(obj));
    }

    isInFrameHierarchy(targetFrame, layoutFrame, recursionDepth = 0): boolean {
      // Check if we've exceeded the maximum recursion depth
      if (!targetFrame.container || recursionDepth > 100) {
        return false;
      }

      if (targetFrame.container === layoutFrame.id) {
        return true;
      }

      const parent = this.canvas
        .getObjects()
        .find(obj => obj.id === targetFrame.container);

      if (!parent) {
        return false;
      }

      return this.isInFrameHierarchy(parent, layoutFrame, recursionDepth + 1);
    }

    // Check if object is on screen and update edge visibility
    isOnScreen(calculate?: boolean): boolean {
      const pointTL = this.canvas.vptCoords?.tl;
      const pointBR = this.canvas.vptCoords?.br;
      const onScreen = super.isOnScreen(calculate);

      if (pointTL && pointBR) {
        // Based on fabric's isPartiallyOnScreen logic
        const allPointsAreOutside = this.getCoords(true, calculate).every(
          function (point) {
            return (
              (point.x >= pointBR.x || point.x <= pointTL.x) &&
              (point.y >= pointBR.y || point.y <= pointTL.y)
            );
          }
        );
        this.edgesVisible = onScreen && !allPointsAreOutside;
      } else {
        this.edgesVisible = true;
      }
      return onScreen;
    }

    // Event listeners. Named to avoid clashes with other behaviors, otherwise
    // order of behaviors matters, which sucks
    private onFrameMouseDown(eventData: fabric.IEvent<MouseEvent>) {
      this.startMoveWithContents(eventData);
    }

    private onFrameMoving() {
      this.moveWithContents();
    }

    private onFrameMoved(eventData: fabric.IEvent<MouseEvent>) {
      this.movedWithContents(eventData);
    }

    private onFrameScaled() {
      this.removeFromParentFrame();
      this.captureContents();
    }
  };
}

export default FrameBehaviors;
